import React, { Dispatch, SetStateAction } from 'react';
import { crudService } from 'core/services';
import Table, { ColumnProps } from 'antd/lib/table';
import { renderMasterIndex } from 'helpers/ant-design/table';
import nameof from 'ts-nameof.macro';
import { useTranslation } from 'react-i18next';
import { generalColumnWidths, generalLanguageKeys } from 'config/consts';

// Parent Repo
// Parent Class
import { LoginConfiguration } from 'models/LoginConfiguration';
// Class
import { LoginConfigurationContent } from 'models/LoginConfigurationContent';
// Filter Class
import { LoginConfigurationContentFilter } from 'models/LoginConfigurationContentFilter';
// Mapping Class

import { tableService } from 'services';
import Input from 'components/Input/Input';

export interface LoginConfigurationContentTableProps {
  loginConfiguration: LoginConfiguration;
  setLoginConfiguration: Dispatch<SetStateAction<LoginConfiguration>>;
}

function LoginConfigurationContentTable(
  props: LoginConfigurationContentTableProps,
) {
  const [translate] = useTranslation();

  const { loginConfiguration, setLoginConfiguration } = props;

  const [
    loginConfigurationContents,
    setLoginConfigurationContents,
  ] = crudService.useContentTable<
    LoginConfiguration,
    LoginConfigurationContent
  >(
    loginConfiguration,
    setLoginConfiguration,
    nameof(loginConfiguration.loginConfigurationContents),
  );

  const [
    loginConfigurationContentFilter,
    setLoginConfigurationContentFilter,
  ] = React.useState<LoginConfigurationContentFilter>(
    new LoginConfigurationContentFilter(),
  );

  const [
    dataSource,
    pagination,
    ,
    handleTableChange,
  ] = tableService.useLocalTable<
    LoginConfigurationContent,
    LoginConfigurationContentFilter
  >(
    loginConfigurationContents,
    loginConfigurationContentFilter,
    setLoginConfigurationContentFilter,
  );

  const handleChangeInputContent = React.useCallback(
    (content: LoginConfigurationContent) => {
      return (value: string) => {
        const newContents = [...loginConfigurationContents];
        const index = newContents.indexOf(content);
        if (index !== -1) {
          newContents[index].value = value;
        }
        setLoginConfigurationContents(newContents);
      };
    },
    [loginConfigurationContents, setLoginConfigurationContents],
  );

  const columns: ColumnProps<LoginConfigurationContent>[] = React.useMemo(
    () => [
      {
        title: translate(generalLanguageKeys.columns.index),
        key: nameof(generalLanguageKeys.columns.index),
        width: generalColumnWidths.index,
        render: renderMasterIndex<LoginConfigurationContent>(pagination),
      },
      {
        title: translate('loginConfigurations.contents.code'),
        key: nameof(dataSource[0].code),
        dataIndex: nameof(dataSource[0].code),
        render(loginConfigurationContent: LoginConfigurationContent) {
          return loginConfigurationContent?.code;
        },
      },
      {
        title: translate('loginConfigurations.contents.name'),
        key: nameof(dataSource[0].name),
        dataIndex: nameof(dataSource[0].name),
        render(name: string) {
          return name;
        },
      },
      {
        title: translate('loginConfigurations.contents.value'),
        key: nameof(dataSource[0].value),
        dataIndex: nameof(dataSource[0].value),
        render(...[value, loginConfigurationContent]) {
          return (
            <Input
              value={value}
              type={
                loginConfigurationContent?.code === 'client_id' ||
                loginConfigurationContent?.code === 'client_secret'
                  ? 'password'
                  : 'text'
              }
              onChange={handleChangeInputContent(loginConfigurationContent)}
              placeholder={translate(
                'loginConfigurations.contents.placeHolder.value',
              )}
            />
          );
        },
      },
    ],
    [dataSource, handleChangeInputContent, pagination, translate],
  );

  return (
    <>
      <Table
        tableLayout="fixed"
        bordered={false}
        size="small"
        columns={columns}
        dataSource={dataSource}
        pagination={pagination}
        rowKey={nameof(
          loginConfigurationContents[0].loginConfigurationContentId,
        )}
        onChange={handleTableChange}
      />
    </>
  );
}
export default LoginConfigurationContentTable;
